import React from 'react'
import g from 'glamorous'
import * as moment from 'moment'
import { Button } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'

import { IColumn } from './Datagrid'
import { Loan } from './queries/LoansQuery'
import { UserSelect } from './UserSelect'
import IntervalTypes from '../constants/IntervalTypes'
import { moneyFormat } from '../utils/format'
import ToPayOverlay from './ToPayOverlay'
import { calculatePayedInterest, calculateRequiredPayment } from '../utils/transactions'
import { BASIC } from '../constants/DateFormat'

const TdRight = g.td(({ theme }) => ({
  textAlign: 'right'
}))

const UserSelectHeader = (props) => <div style={{minWidth: 120}}><UserSelect {...props} /></div>

const loanColumns: {
  date: IColumn<Loan>
  creditorDebtors: IColumn<Loan>
  creditor: IColumn<Loan>
  debtors: IColumn<Loan>
  interval: IColumn<Loan>
  description: IColumn<Loan>
  interest: IColumn<Loan>
  loanedAmount: IColumn<Loan>
  toPay: IColumn<Loan>
  detailAction: IColumn<Loan>
  payedInterest: IColumn<Loan>
  payed: IColumn<Loan>
} = {
  date: {
    id: 'date',
    label: 'Datum',
    cell: ({ data }) => <td>{moment(data.startedOn).format(BASIC)}</td>,
    sort: (a, b) =>
      moment(b.startedOn).valueOf() - moment(a.startedOn).valueOf()
  },
  creditorDebtors: {
    id: 'creditor-debtors',
    label: 'Věřitel / Dlužníci',
    cell: ({ data }) => (
      <td>
        {data.creditor.name} /{' '}
        {data.debtors.map(debtor => debtor.name).join(', ')}
      </td>
    ),
    filter: (value, data) => {
      if (value == null || value.length === 0) {
        return true
      }

      const hash = value.reduce(
        (result, item) => ((result[item.value] = true), result),
        {}
      )

      return (
        hash[data.creditor.id] ||
        data.debtors.some(debtor => hash[debtor.id] === true)
      )
    },
    filterHeader: (value, onChange) => (
      <UserSelectHeader
        autoFocus={true}
        multi={true}
        onChange={onChange}
        value={value == null ? [] : value}
      />
    )
  },
  creditor: {
    id: 'creditor',
    label: 'Věřitel',
    cell: ({ data }) => <td>{data.creditor.name}</td>,
    filter: (value, data) => {
      return value == null || value.value === data.creditor.id
    },
    filterHeader: (value, onChange) => (
      <UserSelectHeader
        autoFocus={true}
        onChange={onChange}
        value={value == null ? [] : value}
      />
    )
  },
  debtors: {
    id: 'debtors',
    label: 'Dlužníci',
    cell: ({ data }) => (
      <td>{data.debtors.map(debtor => debtor.name).join(', ')}</td>
    ),
    filter: (value, data) => {
      if (value == null || value.length === 0) {
        return true
      }

      const hash = value.reduce(
        (result, item) => ((result[item.value] = true), result),
        {}
      )

      return data.debtors.some(debtor => hash[debtor.id] === true)
    },
    filterHeader: (value, onChange) => (
      <UserSelectHeader
        autoFocus={true}
        multi={true}
        onChange={onChange}
        value={value == null ? [] : value}
      />
    )
  },
  interval: {
    id: 'interval',
    label: 'Četnost splátek',
    cell: ({ data }) => (
      <td>{data.interval != null ? IntervalTypes[data.interval] : ''}</td>
    )
  },
  description: {
    id: 'description',
    label: 'Účel',
    sort: (a, b) => (a.description || '').localeCompare(b.description || ''),
    cell: ({ data }) => <td>{data.description}</td>
  },
  interest: {
    id: 'interest',
    label: 'Úrok',
    cell: ({ data }) => <TdRight>{data.state.interest}%</TdRight>
  },
  loanedAmount: {
    id: 'loaned-amount',
    label: 'Jistina',
    cell: ({ data }) => (
      <TdRight>{moneyFormat(data.state.loanedAmount || 0)}</TdRight>
    )
  },
  toPay: {
    id: 'to-pay',
    label: 'Zbývá zaplatit (k dnešnímu dni)',
    cell: ({ data }) => (
      <TdRight>
        <ToPayOverlay loan={data} date={moment()} />
      </TdRight>
    )
  },
  detailAction: {
    id: 'actions',
    label: '',
    cell: ({ data }) => (
      <td>
        <LinkContainer to={`/loans/${data.id}`}>
          <Button bsStyle="link">Detail</Button>
        </LinkContainer>
      </td>
    )
  },
  payedInterest: {
    id: 'payed-interest',
    label: 'Zaplaceno na úrocích',
    cell: ({ data }) => (
      <TdRight>{moneyFormat(calculatePayedInterest(data))}</TdRight>
    )
  },
  payed: {
    id: 'payed-interest',
    label: 'Splátka',
    cell: ({ data }) => (
      <TdRight>
        {moneyFormat(data.transactions.reduce(
          (sum, transaction) =>
            sum +
            (transaction.__typename === 'PaymentTransaction'
              ? transaction.amount || 0
              : 0),
          0
        ))}
      </TdRight>
    )
  }
}

const getRowClassName = (data) => {
  if (data.transactions.find(
      transaction => transaction.__typename === 'CloseTransaction'
  ) != null) {
    return 'strikeout'
  }

  const requiredPayment = calculateRequiredPayment(data, moment())

  if (requiredPayment === 0) { return 'success' }
  if (requiredPayment < 0) { return 'info' }

  return 'warning'
}

const loanRowContainer = ({ data, children }) => (
  <tr
    key={data.id}
    className={getRowClassName(data)}
  >
    {children}
  </tr>
)

export const loan = { columns: loanColumns, rowContainer: loanRowContainer }



// WEBPACK FOOTER //
// ./src/components/DatagridCommons.tsx